import {
  clearReceiptPrinterConfigStorage,
  RECEIPT_PRINTER_STORAGE_KEY,
  writeReceiptPrinterConfigStorage,
  type KeyValueStorage,
} from '../../../printing/receiptPrinterStorage';
import {
  DEFAULT_RECEIPT_PAPER_SIZE,
  isReceiptPaperSize,
  type ReceiptPaperSize,
} from '../../../printing/thermalReceiptTypes';

export type { ReceiptPaperSize };

export const TERMINAL_SETUP_STORAGE_KEY = 'incashflow.posSetup';
export const TERMINAL_STORE_NAME = 'QalamShop';
export const TERMINAL_SETUP_CHANGED_EVENT = 'incashflow:terminal-setup-changed';
export const TERMINAL_RESET_EVENT = 'incashflow:terminal-reset';

const CASHIER_SESSION_KEY = 'cashier_session';
const CASHIER_REMEMBER_KEY = 'cashier_remember_me';

export type TerminalSetup = {
  storeName: string;
  branchId: string;
  branchName: string;
  branchAddress: string;
  receiptPrinterName: string | null;
  barcodePrinterName: string | null;
  /** ESC/POS layout; defaults to 80mm when omitted (legacy setups). */
  receiptPaperSize?: ReceiptPaperSize;
  /** Shelf label: print product name. Default true when omitted. */
  barcodeIncludeName?: boolean;
  /** Shelf label: print PV TTC. Default true when omitted. */
  barcodeIncludePrice?: boolean;
  setupBy: string;
  setupAt: string;
};

function normalizeBarcodeFlag(value: unknown, defaultValue = true): boolean {
  return typeof value === 'boolean' ? value : defaultValue;
}

function normalizeReceiptPaperSize(value: unknown): ReceiptPaperSize {
  return isReceiptPaperSize(value) ? value : DEFAULT_RECEIPT_PAPER_SIZE;
}

export type SetupBranch = {
  visibleId: string;
  name: string;
  address: string;
  isActive: boolean;
};

export type WizardStep = 1 | 2 | 3 | 4;

function resolveStorage(backend?: KeyValueStorage | null): KeyValueStorage | null {
  if (backend !== undefined) return backend;
  if (typeof globalThis.localStorage === 'undefined') return null;
  return globalThis.localStorage;
}

export function canStartTerminalSetup(role: string): boolean {
  return role === 'manager' || role === 'admin';
}

export function branchesForSetup<T extends { isActive: boolean }>(
  role: string,
  branches: T[],
): T[] {
  if (!canStartTerminalSetup(role)) return [];
  return branches.filter((branch) => branch.isActive);
}

export function terminalEntry(setup: TerminalSetup | null): 'wizard' | 'cashier-login' {
  return setup ? 'cashier-login' : 'wizard';
}

export function cashierLoginRequest(
  setup: TerminalSetup,
  username: string,
  password: string,
): {
  username: string;
  password: string;
  branchId: string;
  showBranchField: false;
} {
  return {
    username,
    password,
    branchId: setup.branchId,
    showBranchField: false,
  };
}

export function advanceFromBranch(backToConfirm: boolean): WizardStep {
  return backToConfirm ? 4 : 3;
}

export function advanceFromPrinters(backToConfirm: boolean): WizardStep {
  void backToConfirm;
  return 4;
}

export function editSection(section: 'branch' | 'printers'): {
  step: WizardStep;
  backToConfirm: true;
} {
  return { step: section === 'branch' ? 2 : 3, backToConfirm: true };
}

function isTerminalSetup(value: unknown): value is TerminalSetup {
  if (!value || typeof value !== 'object') return false;
  const setup = value as TerminalSetup;
  return (
    typeof setup.storeName === 'string' &&
    setup.storeName.trim().length > 0 &&
    typeof setup.branchId === 'string' &&
    setup.branchId.trim().length > 0 &&
    typeof setup.branchName === 'string' &&
    typeof setup.branchAddress === 'string'
  );
}

export function readTerminalSetup(backend?: KeyValueStorage | null): TerminalSetup | null {
  const storage = resolveStorage(backend);
  if (!storage) return null;
  const raw = storage.getItem(TERMINAL_SETUP_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as TerminalSetup;
    if (!isTerminalSetup(parsed)) return null;
    return {
      ...parsed,
      receiptPrinterName: parsed.receiptPrinterName?.trim() || null,
      barcodePrinterName: parsed.barcodePrinterName?.trim() || null,
      receiptPaperSize: normalizeReceiptPaperSize(parsed.receiptPaperSize),
      barcodeIncludeName: normalizeBarcodeFlag(parsed.barcodeIncludeName, true),
      barcodeIncludePrice: normalizeBarcodeFlag(parsed.barcodeIncludePrice, true),
      setupBy: parsed.setupBy ?? '',
      setupAt: parsed.setupAt ?? '',
    };
  } catch {
    return null;
  }
}

export function persistTerminalSetup(
  setup: TerminalSetup,
  backend?: KeyValueStorage | null,
): void {
  const storage = resolveStorage(backend);
  if (!storage) return;
  storage.setItem(TERMINAL_SETUP_STORAGE_KEY, JSON.stringify(setup));
  if (setup.receiptPrinterName) {
    writeReceiptPrinterConfigStorage(
      {
        printerName: setup.receiptPrinterName,
        configuredAt: setup.setupAt,
        configuredBy: setup.setupBy,
        paperSize: normalizeReceiptPaperSize(setup.receiptPaperSize),
      },
      storage,
    );
  } else {
    clearReceiptPrinterConfigStorage(storage);
  }
}

export function resetTerminal(backend?: KeyValueStorage | null): void {
  const storage = resolveStorage(backend);
  if (!storage) return;
  storage.removeItem(TERMINAL_SETUP_STORAGE_KEY);
  storage.removeItem(RECEIPT_PRINTER_STORAGE_KEY);
  storage.removeItem(CASHIER_SESSION_KEY);
  storage.removeItem(CASHIER_REMEMBER_KEY);
}
